/**
 * ERP Stock Import Renderer (ERP-ONBOARDING slice 9, design O12 / O13).
 *
 * The item renderer's twin, plus the one thing a stock sweep has that a catalogue import
 * does not: a COVERAGE statement.
 *
 * ── Why coverage is rendered at all ──────────────────────────────────────────
 * A stock sweep only ever reads MAPPED Odoo warehouses. Three detected, one mapped, and
 * the run is completely correct AND covers a third of the seller's depots — with a fully
 * green bar, because the denominator is derived from the same scoped read. Nothing in the
 * progress bar can express that, so the row carries `mappedContainerCount` /
 * `detectedContainerCount` and this renderer reads "2 of 3 ERP warehouses". A run at
 * full coverage says so too: the operator should never have to infer the difference
 * between "all of them" and "the ones you happened to map".
 *
 * ── And why the four buckets ─────────────────────────────────────────────────
 * `successCount` alone is a trap here for the same reason as on the item import: the
 * aggregate's equality damper drops every (item, warehouse) already at the right value,
 * so the second run of a perfectly healthy sweep imports zero rows. `unresolved` is the
 * bucket that deserves attention — ERP products with no Teselly item, which the sweep
 * deliberately did NOT mint (O10), i.e. "import your catalogue first".
 *
 * ── Read the counts through the SHARED selectors, never the raw row fields ────
 * Only `markCompleted` ever writes `successCount` / `errorCount` onto the row; an ERRORED
 * run never reaches it (`createBatchProgressAdvancer.recordError` writes only
 * `{ completedCount, erroredTargetIds, status, updatedAt }`). Reading the raw fields
 * renders 0/0/0/0 exactly when something went wrong, directly under a bar showing the
 * real number. Slice 5 shipped that bug and fixed it; this renderer inherits the fix
 * rather than the bug.
 *
 * Data shape (from ErpStockImportBatchProcessStartedEventSchema):
 *   connectorId: string
 *   integrationUserId: string
 *   mappedContainerCount: number
 *   detectedContainerCount: number
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconBuildingWarehouse } from '@tabler/icons-react';
import { batchProcessesTestIds } from '@domains/batchProcesses/testids';
import type { ProgressProcess } from '../../../shared/state/progressSelectors';
import { renderErpImportBreakdown } from './ErpImportBreakdown';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const data = ( process.data || {}) as ProcessData;
  // `ProgressProcess` is the readmodel row PLUS the completion-carried counters. Every
  // added member is optional, so a plain row widens to it without a cast — and this
  // renderer reads it through the same selectors the progress bar uses, so neither can
  // quietly invent its own fallback rule.
  const counters: ProgressProcess = process;

  const mapped = Number(( data as Record<string, unknown> ).mappedContainerCount ?? 0 );
  const detected = Number(( data as Record<string, unknown> ).detectedContainerCount ?? 0 );

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t(
          'batchProcesses_operations_ErpStockImport_description',
          'Importación del stock del ERP en cada depósito mapeado.'
        )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_sourceErp', 'ERP de origen' )}:
        </Text>
        <ThemeIcon size="sm" variant="light" color="gray">
          <IconBuildingWarehouse size={14} />
        </ThemeIcon>
        <Text fz="sm">
          {data.connectorId ?? t( 'batchProcesses_renderer_none' )}
        </Text>
      </Group>

      {/*
        * THE COVERAGE LINE (O12). Rendered whether or not the mapping is partial: a run
        * at full coverage saying "3 of 3" is what makes "1 of 3" legible when it happens,
        * and an operator must never have to infer it from a green bar.
        */}
      <Group gap="xs" data-testid={batchProcessesTestIds.erpStockImportCoverage}>
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_erpStockCoverage', 'Depósitos del ERP' )}:
        </Text>
        <Text
          fz="sm"
          c={detected > mapped ? 'var(--mantine-color-orange-6)' : undefined}
        >
          {t( 'batchProcesses_renderer_erpStockCoverageValue', {
            mapped,
            detected,
            defaultValue: `${ mapped } de ${ detected } mapeados`
          })}
        </Text>
      </Group>

      {/* SHARED with the item and price renderers — see `ErpImportBreakdown.tsx`. */}
      {renderErpImportBreakdown( counters, t )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t ) => {
    const connectorId = data?.connectorId;
    if ( connectorId ) {
      return t( 'batchProcesses_summary_fromErp', { erp: connectorId, defaultValue: `Desde ${ connectorId }` });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  // The targets are (item, warehouse) pairs, and the row carries neither list — the
  // shared reference-data resolver has nothing to resolve.
  getRequiredReferenceIds: () => ({})
};

export default DetailComponent;
